import { z } from "zod";
import { resolveActiveGeminiModel } from "@atriam/contracts/ai";

import { isKnownModel, knownModelIds } from "@/lib/ai/models";
import { DEFAULT_LLM_MODEL } from "@/lib/features/ai-features/shared/openai-models";
import {
  normalizeEnvString,
  optionalLongSecret,
  optionalSecret,
  optionalUrl,
  requiredUrl,
} from "./_shared";

/**
 * A model id that MODELS actually prices.
 *
 * Three env vars can swap a model without a code deploy, which is the point —
 * but an id outside the pricing table silently bills at FALLBACK_PRICING
 * ($15/$75 opus-grade). Those phantom charges feed the daily cost guard, so a
 * typo here does not merely misreport: it trips the cap and stops the agent.
 * That is exactly how 543 bank-transaction runs died on 2026-07-30.
 *
 * Failing at boot with the valid list is strictly better than discovering it in
 * a billing graph a week later.
 */
function pricedModel() {
  return z
    .string()
    .min(1)
    .transform(resolveActiveGeminiModel)
    .refine(isKnownModel, {
      message: `model has no pricing row, so it would bill at the opus-grade fallback and trip the agent cost cap. Add it to MODELS in lib/ai/models.ts, or use one of: ${knownModelIds().join(", ")}`,
    });
}

const serverSchema = z.object({
  SUPABASE_SERVICE_ROLE_KEY: optionalSecret,
  SUPABASE_DB_URL: optionalUrl,
  CSLB_API_KEY: optionalSecret,
  // Direct SQL connection for `@atriam/db` (Kysely over pg), the typed data
  // layer that replaces PostgREST call sites domain by domain
  // (docs/plans/2026-09-04-supabase-exit-research.md §4.2/§4.3). Points at
  // PgBouncer in transaction mode (6432) from the app, or at Postgres 5432
  // for migrations. Optional so nothing boots differently until a domain
  // actually cuts over; `getDb()` in @/lib/db throws when it is read unset.
  DATABASE_URL: optionalUrl,
  // PEM certificate of the private CA that signed the database's server
  // certificate. Handed straight to `tls.connect`, which is what makes
  // `sslmode=verify-full` possible without a public root — there is no
  // renewal hook to forget, only a redeploy when the CA rolls.
  DATABASE_SSL_CA: optionalSecret,
  // Direct 5432 connection for the Deals module (schema `deals`), read by
  // @atriam/deals-engine/db.ts itself from process.env: the engine is shared
  // with the droplet worker, so it cannot import this barrel. Listed here so
  // the values are validated and documented with the rest. Unset, the module
  // still builds and imports; its pages and routes fail on the first query.
  DEALS_DATABASE_URL: optionalUrl,
  // PEM of the private CA for DEALS_DATABASE_URL (verify-full), the same
  // value as DATABASE_SSL_CA. `\n` escapes are accepted.
  DEALS_DATABASE_SSL_CA: optionalSecret,
  TELEPHONY_ENCRYPTION_KEY: optionalSecret,
  // SiteCall telephony application API (docs/plans/2026-10-09-telephony-
  // sitecall-implementation.md B3/B4): Atriam provisions every number through
  // it. Origin only, e.g. https://sitecall.ai. Optional so the app boots
  // without them; the client throws `not_configured` when it is used unset.
  SITECALL_APPLICATION_API_URL: optionalUrl,
  SITECALL_APPLICATION_SECRET_ATRIAM: optionalSecret,
  // Atriam's SiteCall /v1 application key (sc_live_…, scope metrics:read):
  // the Team performance page reads GET /api/v1/metrics with it at
  // SITECALL_APPLICATION_API_URL. Unset = the page says "not connected".
  SITECALL_API_KEY_ATRIAM: optionalSecret,
  // HMAC-SHA256 secret SiteCall's voice host signs its caller lookups with
  // (`<ts>.<body>` → /api/internal/sitecall/caller-name). Same value as the
  // SiteCall gateway's SITECALL_TELEPHONY_EVENTS_SECRET (the atriam
  // application's events_secret_env). List form for zero-downtime rotation;
  // with neither set the route answers 500.
  SITECALL_TELEPHONY_EVENTS_SECRET: optionalSecret,
  SITECALL_TELEPHONY_EVENTS_SECRETS: optionalSecret,
  // The signing secret (whsec_…) of Atriam's SiteCall webhook endpoint
  // (POST /api/v1/webhooks at SiteCall, returned once at create/rotate):
  // voice event deliveries carry x-sitecall-timestamp and are signed over
  // `<timestamp>.<body>` with it. List form for rotation. Unset = timestamped
  // deliveries answer 401 and SiteCall retries them.
  SITECALL_EVENTS_WEBHOOK_SECRET: optionalSecret,
  SITECALL_EVENTS_WEBHOOK_SECRETS: optionalSecret,
  RESEND_API_KEY: optionalSecret,
  RESEND_FROM_EMAIL: optionalSecret,
  RESEND_AUDIENCE_ID: optionalSecret, // Resend Audience for the marketing newsletter signup
  // --- Project inbound email (lib/features/project-inbound-email) ---
  // The receiving subdomain whose MX record points at Resend, e.g. in.atriam.ai
  // (never the root domain: its MX belongs to Google Workspace). Each project's
  // address is p-<token>@<this>. Unset = the feature is invisible and the
  // webhook answers 503.
  INBOUND_EMAIL_DOMAIN: optionalSecret,
  // Svix signing secret (whsec_…) of the Resend `email.received` webhook that
  // targets /api/webhooks/email-inbound. Reading the mail also needs a
  // RESEND_API_KEY with receiving access.
  RESEND_WEBHOOK_SECRET: optionalSecret,
  // --- Contractor outreach (lib/features/contractor-outreach) ---
  // Comma-separated addresses the outreach send path may email while
  // lib/flags.ts contractorOutreachSending is false: the owner's test inbox,
  // never a contractor. "Send test" on /admin/contractor-outreach goes here.
  OUTREACH_TEST_RECIPIENTS: optionalSecret,
  // Dedicated cold-outreach sender on its own subdomain, e.g.
  // "Atriam <hello@go.atriam.ai>", so cold mail never touches the
  // transactional domain's reputation. Real sends refuse without it, or when
  // it shares RESEND_FROM_EMAIL's domain.
  OUTREACH_FROM_EMAIL: optionalSecret,
  // The physical postal address CAN-SPAM requires in every commercial
  // email's footer: street, city, state and ZIP (the footer prefixes the
  // company name). Real sends refuse without it.
  OUTREACH_POSTAL_ADDRESS: optionalSecret,
  // Signs one-click unsubscribe tokens. Every outreach send (test sends too)
  // refuses without it; rotating it breaks the links in mail already sent.
  OUTREACH_UNSUBSCRIBE_SECRET: optionalLongSecret,
  STRIPE_SECRET_KEY: optionalSecret,
  STRIPE_BILLING_RETURN_URL: optionalSecret,
  STRIPE_ATRIAM_PORTAL_CONFIGURATION_ID: optionalSecret,
  STRIPE_ATRIAM_PORTAL_SWITCH_CONFIGURATION_ID: optionalSecret,
  STRIPE_ATRIAM_PORTAL_LEGACY_CONFIGURATION_ID: optionalSecret,
  // Stripe Connect (Accounts v2)
  // Required at boot — the trial subsystem must succeed when a workspace is
  // created. Misconfiguration surfaces at deploy, not at first signup.
  STRIPE_PLATFORM_WEBHOOK_SECRET: z.preprocess(
    normalizeEnvString,
    z.string().min(1),
  ), // whsec_xxx for platform endpoint
  // Legacy Pro $49/$468 IDs remain available for existing subscriptions only.
  // New checkout never falls back to these IDs.
  STRIPE_PLATFORM_PRICE_PRO_MONTHLY_ID: optionalSecret,
  STRIPE_PLATFORM_PRICE_PRO_ANNUAL_ID: optionalSecret,
  // Apply the additive allowance migration before enabling enforcement.
  ATRIAM_AI_PLAN_LIMITS_ENABLED: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  // The nightly reaper only reports orphaned signed-W-9 PDFs until this is
  // "true"; then it deletes them. Turn on after reviewing a report.
  SIGNED_W9_ORPHAN_DELETE_ENABLED: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  // Device search export is an explicit rollout; existing search stays available.
  LOCAL_SEARCH_EXPORT_ENABLED: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  // September 2026 catalog: Pro $129/$1188, Business $179/$1668.
  // Optional at boot; checkout fails closed until each price is provisioned.
  STRIPE_PLATFORM_PRICE_PRO_2026_MONTHLY_ID: optionalSecret,
  STRIPE_PLATFORM_PRICE_PRO_2026_ANNUAL_ID: optionalSecret,
  STRIPE_PLATFORM_PRICE_BUSINESS_MONTHLY_ID: optionalSecret,
  STRIPE_PLATFORM_PRICE_BUSINESS_ANNUAL_ID: optionalSecret,
  // Pay-what-you-want "Support Atriam" price (custom_unit_amount, $1 minimum) for free workspaces.
  STRIPE_PLATFORM_PRICE_DONATION_ID: optionalSecret,
  STRIPE_CONNECT_WEBHOOK_SECRET: optionalSecret, // whsec_xxx — connected-accounts scope (money events)
  STRIPE_CONNECT_ACCOUNT_WEBHOOK_SECRET: optionalSecret, // whsec_xxx — your-account scope (v2 account events)
  OPENAI_API_KEY: optionalSecret,
  // Vertex AI Express key (AQ.) for the RAG image leg: gemini-embedding-001
  // (caption @1536) + multimodalembedding@001 (image/query @1408). Dormant until set.
  GEMINI_API_KEY: optionalSecret,
  // ── RAG (Phase 1a + later phases) ─────────────────────────────────────
  // HMAC secret shared by the pg_cron trigger and the former /api/internal/rag-tick
  // route. Pattern matches AGENT_INTERNAL_WEBHOOK_SECRET. List form for
  // zero-downtime rotation lands in a follow-up phase if needed.
  // rag-index pipeline (Phase 2 "Index project" expensive pass). Dormant until set.
  RAG_INDEX_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  // Signs the spend-check cron poke → /api/internal/spend-check (90% alert).
  // Absent → seed blanks the app_settings rows → the cron no-ops (dormant).
  SPEND_CHECK_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  // Signs the droplet's agent-CLI usage push → /api/internal/agent-cli-usage,
  // which backs the /usage Telegram command. Unlike the pg_cron ticks the
  // signer is the DigitalOcean droplet (the only host with the `claude`
  // binary), so this secret is provisioned on the droplet, not in app_settings.
  // Absent → the route 500s and the collector no-ops (dormant). List form for
  // zero-downtime rotation.
  AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Signs /api/internal/data-stash calls (monthly CSLB/DCA reference-data
  // relay: GitHub Actions uploads, droplet data-sync downloads — CDT
  // firewalls the droplet's egress IP). List form for rotation.
  DATA_STASH_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  DATA_STASH_INTERNAL_WEBHOOK_SECRETS: optionalSecret,

  // Model identifier passed to the autopilot agent loop's OpenAI Responses
  // API calls. Single source of truth — repository.createSession requires
  // this and the arm endpoint reads it from env. Update here to roll the
  // agent forward to a new model without code changes.
  OPENAI_AGENT_MODEL: pricedModel().default(DEFAULT_LLM_MODEL),
  // Model id for the pg_cron 1-min agent-step dispatcher (lib/agent-runtime/
  // step-runner.ts). Falls back to DEFAULT_LLM_MODEL when unset — override to
  // roll the step loop onto a new model without a code deploy.
  AGENT_STEP_MODEL: pricedModel().optional(),
  AGENT_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  // Optional comma-separated list of accepted secrets for zero-downtime
  // rotation. When set, the route accepts a signature matching ANY listed
  // secret in addition to AGENT_INTERNAL_WEBHOOK_SECRET. See
  // "Telephony autopilot setup" in CLAUDE.md for the rotation procedure.
  AGENT_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Small-jobs pipeline (inbound customer text on a job's conversation →
  // /api/internal/job-pipeline-tick). Single + list form, same rotation
  // pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S].
  JOB_PIPELINE_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  JOB_PIPELINE_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the Postgres trigger on public.leads →
  // /api/internal/lead-notify (per-workspace Telegram new-lead alerts). Must
  // match `lead_notify_webhook_secret` row in public.app_settings. List form
  // supports zero-downtime rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Single shared Telegram bot token for multi-channel notification targets
  // (app.notification_targets). Distinct from per-workspace SIGNOZ/* ops bots.
  TELEGRAM_LEAD_BOT_TOKEN: optionalSecret,
  // Public @username of the shared notification bot, used to build the deep
  // link (https://t.me/<username>?start=<nonce>) the Settings connect flow opens.
  TELEGRAM_LEAD_BOT_USERNAME: optionalSecret,
  // Secret-token the Telegram Bot API echoes in the x-telegram-bot-api-secret-token
  // header on every webhook delivery. The /api/webhooks/telegram route rejects
  // any request whose header doesn't match this value before parsing the body.
  TELEGRAM_WEBHOOK_SECRET: optionalSecret,
  // Shared HMAC secret for the Postgres trigger → /api/internal/transcribe-call
  // path. Must match `transcribe_webhook_secret` row in public.app_settings.
  // Single + list form supports zero-downtime rotation (same pattern as
  // AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  TRANSCRIBE_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  TRANSCRIBE_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the Postgres trigger → /api/internal/analyze-call
  // path. Must match `analyze_webhook_secret` row in public.app_settings.
  ANALYZE_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  ANALYZE_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the price_range_tick pg_cron →
  // /api/internal/price-range-tick path. Must match the
  // `price_range_webhook_secret` row in public.app_settings.
  PRICE_RANGE_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  PRICE_RANGE_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the workspace_calls AFTER INSERT OR UPDATE trigger →
  // /api/internal/attribute-call path. Fires when a call gains a lead_id and
  // the phone_number_id matches a campaign tracking number. Must match the
  // `attribute_call_webhook_secret` row in public.app_settings. Single + list
  // form supports zero-downtime rotation (same pattern as ANALYZE_INTERNAL_WEBHOOK_SECRET[S]).
  ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the pg_cron → /api/internal/banking-sync-tick path
  // (Plaid transaction backfill cron). Must match the `banking_webhook_secret`
  // row in public.app_settings.
  BANKING_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  // Shared HMAC secret for the monthly pg_cron → /api/internal/verification-recheck
  // path. Re-evaluates Plaid-backed needs_info claims against the fresh CSLB
  // snapshot and auto-promotes strong matches to verified. Must match the
  // `verification_recheck_webhook_secret` row in public.app_settings. Single +
  // list form supports zero-downtime rotation (same pattern as
  // AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for the daily pg_cron (compliance_tick_daily) →
  // former /api/internal/compliance-tick path (now the compliance dispatch handler). Derives/reconciles compliance
  // obligation rows per workspace and dispatches due/overdue notifications.
  // Must match the `compliance_webhook_secret` row in public.app_settings.
  // Single + list form supports zero-downtime rotation (same pattern as
  // AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  // Shared HMAC secret for the pg_cron → /api/internal/statements-tick path
  // (Plaid statements fetch + parse worker). Must match the
  // `statements_webhook_secret` row in public.app_settings. Single + list
  // form supports zero-downtime rotation (same pattern as
  // AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  STATEMENTS_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  STATEMENTS_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  SEND_SMS_HOOK_SECRET: optionalSecret,
  AUTH_OTP_GATEWAY_SECRET: optionalSecret,
  // Comma-separated E.164 numbers that take the deterministic-OTP test path
  // during phone-verified signup (mirrors the gateway's AUTH_OTP_TEST_NUMBERS
  // short-circuit). Optional; empty in prod unless an operator seeds a
  // controlled test number, so real signups stay unaffected.
  AUTH_OTP_TEST_NUMBERS: z.string().optional(),
  // Comma-separated emails that get a personal dev-preview of the app: every
  // module is visible to these accounts at all times (bypassing the release
  // gate + tier), each tagged "Dev" in the sidebar. Empty in prod by default.
  DEV_PREVIEW_EMAILS: z.string().optional(),
  // Local, read-only browser verification uses the dedicated demo identity.
  VERIFY_USER_EMAIL: z.email().optional(),
  // /api/internal/notify-event — HMAC-signed ops-event callbacks (missed_call,
  // agent_escalated/failed) the gateway/web fan to telegram + sms. List form
  // supports zero-downtime rotation (same pattern as LEAD_NOTIFY_*).
  NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Atriam's receptionist secret at SiteCall (Voice Phase 3 Task 11): the
  // voice-agent signs a SiteCall voice-engine call's requests to
  // /api/internal/voice-agent/* with it. Must equal the droplet's
  // VOICE_AGENT_APP_SECRET_ATRIAM (deploy.sh forwards the root .env value).
  // List form for zero-downtime rotation.
  VOICE_AGENT_APP_SECRET_ATRIAM: optionalSecret,
  VOICE_AGENT_APP_SECRETS_ATRIAM: optionalSecret,
  // Ticket-signing secret for services/sse-bridge (the LISTEN/NOTIFY → SSE
  // replacement for Supabase Realtime). The ticket route signs
  // base64url({w,u,exp}) with this; the bridge verifies with the same value in
  // its REALTIME_BRIDGE_SECRETS. List form supports zero-downtime rotation:
  // put the new secret in _SECRET here and both in _SECRETS on the bridge,
  // deploy, then drop the old one. Optional at schema level so the app boots
  // with the bridge unbuilt — the ticket route 503s instead of the app failing.
  REALTIME_BRIDGE_SECRET: optionalSecret,
  REALTIME_BRIDGE_SECRETS: optionalSecret,
  // IANA timezone used to compute "today" in the agent system prompt when
  // the workspace doesn't yet carry its own tz column. Without this, the
  // model proposes appointments for the wrong day cross-midnight UTC.
  // Follow-up: add `workspaces.timezone` and remove this fallback.
  AUTOPILOT_DEFAULT_TIMEZONE: z.string().default("America/Los_Angeles"),
  PORTAL_SESSION_SECRET: optionalSecret,
  PAYROLL_ENCRYPTION_KEY: optionalSecret,
  // PAdES seal for signed contracts (lib/features/signing/server/seal.ts):
  // a base64 PKCS#12 and its passphrase. Unset leaves signed PDFs unsealed.
  SIGNING_SEAL_P12_BASE64: optionalSecret,
  SIGNING_SEAL_P12_PASSPHRASE: optionalSecret,
  // Native deep-link verification. APPLE_TEAM_ID is joined with each bundle
  // suffix to build the appIDs in /.well-known/apple-app-site-association;
  // ANDROID_SHA256_CERT_FINGERPRINT is the release signing cert echoed in
  // /.well-known/assetlinks.json. Both optional: unset means the route 404s,
  // which is exactly the "no association file" state — links open in the
  // browser instead of the native app, no breakage.
  APPLE_TEAM_ID: optionalSecret,
  ANDROID_SHA256_CERT_FINGERPRINT: optionalSecret,
  // ProReferral Gateway. Auth is via signed `X-Atriam-Service-Claim`
  // envelope minted with `ATRIAM_SERVICE_CLAIM_SECRET` — no per-service
  // shared secret here.
  PROREFERRAL_GATEWAY_URL: optionalUrl,
  // Google Indexing API service-account key (JSON, as downloaded from Google
  // Cloud). Optional + server-only: the careers-page indexing ping in
  // lib/features/hiring/server/google-indexing.ts stays a dormant no-op until
  // this is set. Declared here so it appears in the single env inventory and is
  // stripped from the client bundle; the two hiring call sites read it raw via
  // process.env by design (per-test injection + the "unconfigured → skip" gate
  // re-reads on every call), so they are exempted in the
  // no-process-env-outside-env-barrel ast-grep rule.
  GOOGLE_INDEXING_SA_JSON: optionalSecret,
  // Google Ads + Local Services Ads (LSA) — per-contractor lead ingestion (Task 1)
  GOOGLE_ADS_DEVELOPER_TOKEN: optionalSecret,
  GOOGLE_ADS_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  GOOGLE_ADS_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Signs the connect OAuth state (lib/api/oauth-state.ts). Dedicated on
  // purpose — never a copy of the tick secret above; >= 32 chars. Unset means
  // /api/integrations/google-ads/{connect,callback} answer 503.
  GOOGLE_ADS_OAUTH_STATE_SECRET: optionalSecret,
  // google-ads-gateway Bun service URL. Prod: https://google-ads-gateway.atriam.ai
  // Set in Vercel — optional so the app boots before the gateway is deployed.
  GOOGLE_ADS_GATEWAY_URL: optionalUrl,
  // Growth (the separate campaign-buying product) connector. Both are
  // server-only JSON and both are optional: unset means the connector is off.
  // GROWTH_ACQUISITION_CONNECTION: one {origin, productOrigin, token,
  // subjectSecret, studioId?} object for the consented visit handoff and the
  // qualified-business / onboarding / subscription receipts
  // (docs/runbooks/growth-acquisition.md). GROWTH_REPORT_CONNECTIONS: an array
  // of {workspaceId, origin, token, studioId?} bindings for the read-only
  // spend panel on /marketing (docs/runbooks/growth-reporting.md).
  GROWTH_ACQUISITION_CONNECTION: optionalSecret,
  GROWTH_REPORT_CONNECTIONS: optionalSecret,
  // Facebook (Meta) account discovery and lead intake, called on Graph
  // directly from lib/features/fb-ads/server/gateway-client.ts. App id/secret
  // authorise the OAuth token exchange + appsecret_proof. Optional so the app
  // boots before the Meta app is verified.
  FACEBOOK_ADS_APP_ID: optionalSecret,
  FACEBOOK_ADS_APP_SECRET: optionalSecret,
  // Signs the connect OAuth state; dedicated, >= 32 chars. Unset means
  // /api/integrations/facebook-ads/{connect,callback} answer 503.
  FACEBOOK_ADS_OAUTH_STATE_SECRET: optionalSecret,
  // verify_token echoed back on Meta's leadgen webhook GET verification
  // challenge (hub.verify_token). The POST signature is the app-secret HMAC
  // (FACEBOOK_ADS_APP_SECRET), not this. Optional so the app boots before the
  // Lead Ads webhook is configured in the Meta dashboard.
  FB_LEADGEN_VERIFY_TOKEN: optionalSecret,
  // Google (server-side only — use separate key restricted by IP, not the NEXT_PUBLIC_ one)
  GOOGLE_GEOCODING_API_KEY: optionalSecret,
  // Server key for static map images (app/api/maps/static answers 500 without it).
  // Test-only lib/google/geocode.ts reads it too; live geocoding is server-geocoder.ts.
  GOOGLE_MAPS_API_KEY: optionalSecret,
  FACEBOOK_GATEWAY_URL: optionalUrl,
  // Google OAuth (Drive file sync + Gmail watch — per-workspace tokens in workspace_lead_integrations)
  GOOGLE_OAUTH_CLIENT_ID: optionalSecret,
  GOOGLE_OAUTH_CLIENT_SECRET: optionalSecret,
  // Google Pub/Sub (Gmail push notifications for real-time lead sync)
  GOOGLE_PUBSUB_TOPIC: optionalSecret,
  GOOGLE_PUBSUB_VERIFICATION_TOKEN: optionalSecret,
  // ElevenLabs Scribe (speech-to-text: call transcription, dictation)
  ELEVENLABS_API_KEY: optionalSecret,
  // AI Gateway routing — override the OpenAI base URL to route through Vercel
  // AI Gateway or any OpenAI-compatible proxy. When unset, clients default to
  // api.openai.com.
  OPENAI_BASE_URL: z.url().optional(),
  // Vercel Cron
  CRON_SECRET: optionalSecret,
  // One secret for every caller outside the database: the droplet gateways and
  // host timers that POST to /api/internal/* (telephony-gateway notify-event,
  // voice-agent call-complete/search/profile, cron-report, data-stash).
  //
  // Audit R1 leaves exactly three secret classes standing — CRON_SECRET
  // (Vercel-managed, rotated atomically at deploy), this one, and vendor
  // secrets — in place of the 32 *_INTERNAL_WEBHOOK_SECRET names, of which
  // every _SECRETS plural was empty in .env, i.e. the rotation lever they
  // existed for was never used.
  //
  // Routes accept it ALONGSIDE their existing per-pipeline secret, so setting
  // it changes nothing until a gateway starts signing with it. 32 chars
  // minimum: unlike the SQL-signed secrets, no seed script generates this, so
  // an operator types it and the floor is what stops a weak one.
  INTERNAL_SERVICE_SECRET: optionalLongSecret,
  // Thumbtack Gateway. Auth is via signed `X-Atriam-Service-Claim` envelope
  // minted with `ATRIAM_SERVICE_CLAIM_SECRET` — no per-service shared
  // secret here. All Thumbtack API keys live inside the gateway.
  THUMBTACK_GATEWAY_URL: optionalUrl,
  // Home Depot Pro Referral app credentials. Optional: the lead-integrations
  // slice is per-workspace opt-in, so an unset value must disable the
  // integration rather than fail the boot of every other route.
  HD_CLIENT_ID: optionalSecret,
  HD_CLIENT_SECRET: optionalSecret,
  // Plaid (bank feed aggregation — second provider alongside Stripe FC).
  // All five required: the integration is production-default with no
  // soft-disable path. Misconfiguration must surface at boot, not at first
  // user click.
  PLAID_CLIENT_ID: z.preprocess(normalizeEnvString, z.string().min(1)),
  PLAID_SECRET: z.preprocess(normalizeEnvString, z.string().min(1)),
  PLAID_ENV: z.preprocess(
    normalizeEnvString,
    z.enum(["sandbox", "production"]),
  ),
  PLAID_WEBHOOK_URL: requiredUrl,
  // Token encryption (Plaid access tokens at rest). 32-byte key, base64.
  BANKING_TOKEN_ENCRYPTION_KEY: z.preprocess(
    normalizeEnvString,
    z.string().min(1),
  ),
  // Active key version. Encrypt prefixes ciphertext with this ID; decrypt
  // looks up the matching key. Bump when rotating; old ID/key pairs go in
  // BANKING_TOKEN_ENCRYPTION_KEYS until legacy rows are re-encrypted.
  BANKING_TOKEN_ENCRYPTION_KEY_ID: z.preprocess(
    normalizeEnvString,
    z.string().min(1),
  ),
  // Optional decrypt-only keys for zero-downtime rotation. JSON object of
  // {"<id>": "<base64-key>", ...}. New rows are always encrypted with the
  // active key above; this map only exists to decrypt rows still tagged
  // with a previous KEY_ID.
  BANKING_TOKEN_ENCRYPTION_KEYS: optionalSecret,
  // QuickBooks Online export (per-workspace OAuth). Both optional: absent =
  // integration hidden and every export path silently no-ops.
  QUICKBOOKS_CLIENT_ID: optionalSecret,
  QUICKBOOKS_CLIENT_SECRET: optionalSecret,
  QUICKBOOKS_ENV: z.preprocess(
    normalizeEnvString,
    z.enum(["sandbox", "production"]).default("production"),
  ),
  // Work Documents — signing secret for signed-URL tokens (PDF generation, etc.).
  // Optional at schema level (this module is imported by client-side code and
  // browser `process.env` for non-NEXT_PUBLIC_* is undefined). Length/presence
  // is validated at call-site in lib/features/documents/server/token.ts.
  WORK_DOCS_TOKEN_SECRET: optionalSecret,
  // Claims — HMAC signing key for the `atriam-claim-pending` cookie that
  // carries the pending claim id across the OTP / Plaid handshake. Required
  // because the claim flow signs cookies in production: an unset secret used
  // to silently fall back to a hardcoded literal at call sites, making every
  // signed cookie forgeable. Validated at boot so misconfiguration surfaces
  // immediately rather than at first user click.
  CLAIM_COOKIE_SECRET: z.preprocess(normalizeEnvString, z.string().min(32)),
  // Community — comma-separated list of emails auto-granted admin on profile create
  COMMUNITY_ADMIN_EMAILS: optionalSecret,
  OTEL_EXPORTER_OTLP_ENDPOINT: optionalUrl,
  SIGNOZ_INGEST_TOKEN: optionalSecret,
  SIGNOZ_TELEGRAM_BOT_TOKEN: optionalSecret,
  SIGNOZ_TELEGRAM_CHAT_ID: optionalSecret,
  // Comma-separated Telegram user IDs allowed to create/revoke complimentary
  // access codes through the ops bot. Empty/unset keeps the workflow disabled.
  BILLING_INVITE_TELEGRAM_USER_IDS: optionalSecret,
  SIGNOZ_TELEGRAM_CRITICAL_CHAT_ID: optionalSecret,
  // In-product user feedback widget → Telegram chat dedicated to product
  // feedback so it doesn't drown in ops alerts. Reuses SIGNOZ_TELEGRAM_BOT_TOKEN
  // (same bot, different chat). Falls back to SIGNOZ_TELEGRAM_CHAT_ID at call
  // site so dev environments with only the alert chat configured still work.
  FEEDBACK_TELEGRAM_CHAT_ID: optionalSecret,
  SIGNOZ_ALERT_WEBHOOK_TOKEN: optionalSecret,
  POSTHOG_ALERT_WEBHOOK_SECRET: optionalSecret,
  // PostHog personal API keys (query read) + project ids for the weekly
  // AI-visibility digest (app/api/cron/ai-visibility-digest). Atriam's project
  // 543076 and SiteCall's 542675 live in separate organisations, so each needs
  // its own key. Either pair absent skips that product.
  POSTHOG_CLI_API_KEY: optionalSecret,
  POSTHOG_CLI_PROJECT_ID: optionalSecret,
  SITECALL_POSTHOG_CLI_API_KEY: optionalSecret,
  SITECALL_POSTHOG_CLI_PROJECT_ID: optionalSecret,
  // Vercel deploy webhook secret — set in Vercel project Webhook config UI
  // and mirrored here. Empty → /api/observability/vercel-deploy-alert returns
  // 503 (the alert path is just disabled).
  VERCEL_WEBHOOK_SECRET: optionalSecret,
  ATRIAM_SERVICE_CLAIM_SECRET: optionalSecret,
  EXEMPT_WORKSPACE_IDS: optionalSecret,
  // Drives the SMS-thread project-attribution sweep. Absent = the pipeline
  // stays dormant: the build hook blanks its app_settings rows and
  // fire_signed_webhook silently no-ops.
  THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRET: optionalLongSecret,
  THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRETS: z.string().optional(),
  // Shared HMAC secret for the Postgres trigger + pg_cron → /api/internal/documents-extract-tick
  // path. Drives the documents classification + extraction worker. Must match
  // the `documents_extract_secret` row in public.app_settings. Single + list
  // form supports zero-downtime rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRET: optionalLongSecret,
  DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRETS: z.string().optional(),
  // Shared HMAC secret for the pg_cron 1-min step dispatcher → /api/internal/agent-step
  // path. Drives the autonomous agent loop one step per HTTP invocation. Single + list
  // form supports zero-downtime rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  AGENT_STEP_INTERNAL_WEBHOOK_SECRET: optionalLongSecret,
  AGENT_STEP_INTERNAL_WEBHOOK_SECRETS: z.string().optional(),
  // Shared HMAC secret for Postgres trigger → /api/internal/agent-signal path.
  // Fired when workspace_calls transitions to 'missed' or 'no_answer' (Job 1 missed-call recovery).
  // Single + list form supports zero-downtime rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRET: optionalLongSecret,
  AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRETS: z.string().optional(),
  // Shared HMAC secret for the pg_cron → /api/internal/agent-memory-tick path.
  // Drives the background memory summarization pipeline. Single + list form
  // supports zero-downtime rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  // Shared HMAC secret for the pg_cron 1-min worker dispatcher →
  // /api/internal/migration-tick path. Drives the data-migration apply +
  // exception-triage worker. Single + list form supports zero-downtime
  // rotation (same pattern as AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  // Shared HMAC secret for the unified dispatch nudge (ops.pipeline_dispatch
  // eager-fire) → /api/internal/dispatch.
  // Must match the `dispatch_webhook_secret` row in public.app_settings.
  // Single + list form supports zero-downtime rotation (same pattern as
  // AGENT_INTERNAL_WEBHOOK_SECRET[S]).
  DISPATCH_INTERNAL_WEBHOOK_SECRET: optionalSecret,
  DISPATCH_INTERNAL_WEBHOOK_SECRETS: optionalSecret,
  // Shared HMAC secret for off-Vercel crons (currently droplet data-sync) that
  // POST their outcome to /api/internal/cron-report so they get an
  // ops.cron_heartbeats row like the Vercel crons do. The external runner signs the
  // raw body with `hmac_sha256(body, secret)` hex and sends it in the
  // `x-cron-report-signature` header. Optional so the app boots before the
  // secret is configured; the route returns 401 until it's set.
  CRON_REPORT_WEBHOOK_SECRET: optionalSecret,
  // Comma-separated user IDs allowed to operate the CCPA DSAR privacy console
  // (review + fulfill data-subject requests). Empty/unset = no operators, so
  // the console fails closed until an operator is seeded.
  PRIVACY_OPERATOR_USER_IDS: z.string().optional(),
  // Comma-separated user IDs allowed into the operator console
  // (/admin/accounts). Empty/unset = nobody, so it fails closed.
  OPERATOR_USER_IDS: z.string().optional(),
  // Bearer the console presents to each product's read-only
  // /api/operator/v1/snapshot (SiteCall checks the same value).
  OPERATOR_API_TOKEN: optionalSecret,
  // Garage (S3) on the droplet, for the `s3` storage backend. All five are
  // optional so their absence never fails a build: `resolveStorageBackend()`
  // falls back to `supabase` unless every one is present, which is what keeps
  // the switch dark until an operator sets them. Names match the droplet's own
  // (infra/atriam-db/docker-compose.yml) so one value serves both sides.
  GARAGE_S3_ENDPOINT: optionalUrl,
  GARAGE_BUCKET: z.string().min(1).optional(),
  GARAGE_REGION: z.string().min(1).optional(),
  GARAGE_ACCESS_KEY_ID: optionalSecret,
  GARAGE_SECRET_ACCESS_KEY: optionalSecret,
});

// Server-only env vars are stripped from the client bundle, so parsing the
// server schema in the browser would always fail on required fields. Gate the
// parse to server execution; on the client the typed stub is never read by
// well-behaved code (server-only modules carry `import "server-only"` to
// catch accidental client imports at build time).
//
// `typeof window === "undefined"` is the production signal. Vitest's jsdom
// environment defines `window`, so we also accept the `VITEST` worker flag
// to treat tests as server runtime — otherwise every test that reads a
// server env value silently sees an empty stub.
const isServerRuntime =
  typeof window === "undefined" || process.env.VITEST === "true";

export const serverEnv = isServerRuntime
  ? serverSchema.parse({
      SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
      SUPABASE_DB_URL: process.env.SUPABASE_DB_URL,
      CSLB_API_KEY: process.env.CSLB_API_KEY,
      DATABASE_URL: process.env.DATABASE_URL,
      DATABASE_SSL_CA: process.env.DATABASE_SSL_CA,
      DEALS_DATABASE_URL: process.env.DEALS_DATABASE_URL,
      DEALS_DATABASE_SSL_CA: process.env.DEALS_DATABASE_SSL_CA,
      TELEPHONY_ENCRYPTION_KEY: process.env.TELEPHONY_ENCRYPTION_KEY,
      SITECALL_APPLICATION_API_URL: process.env.SITECALL_APPLICATION_API_URL,
      SITECALL_APPLICATION_SECRET_ATRIAM:
        process.env.SITECALL_APPLICATION_SECRET_ATRIAM,
      SITECALL_API_KEY_ATRIAM: process.env.SITECALL_API_KEY_ATRIAM,
      SITECALL_TELEPHONY_EVENTS_SECRET:
        process.env.SITECALL_TELEPHONY_EVENTS_SECRET,
      SITECALL_TELEPHONY_EVENTS_SECRETS:
        process.env.SITECALL_TELEPHONY_EVENTS_SECRETS,
      SITECALL_EVENTS_WEBHOOK_SECRET:
        process.env.SITECALL_EVENTS_WEBHOOK_SECRET,
      SITECALL_EVENTS_WEBHOOK_SECRETS:
        process.env.SITECALL_EVENTS_WEBHOOK_SECRETS,
      RESEND_API_KEY: process.env.RESEND_API_KEY,
      RESEND_FROM_EMAIL: process.env.RESEND_FROM_EMAIL,
      RESEND_AUDIENCE_ID: process.env.RESEND_AUDIENCE_ID,
      INBOUND_EMAIL_DOMAIN: process.env.INBOUND_EMAIL_DOMAIN,
      RESEND_WEBHOOK_SECRET: process.env.RESEND_WEBHOOK_SECRET,
      OUTREACH_TEST_RECIPIENTS: process.env.OUTREACH_TEST_RECIPIENTS,
      OUTREACH_FROM_EMAIL: process.env.OUTREACH_FROM_EMAIL,
      OUTREACH_POSTAL_ADDRESS: process.env.OUTREACH_POSTAL_ADDRESS,
      OUTREACH_UNSUBSCRIBE_SECRET: process.env.OUTREACH_UNSUBSCRIBE_SECRET,
      STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
      STRIPE_BILLING_RETURN_URL: process.env.STRIPE_BILLING_RETURN_URL,
      STRIPE_ATRIAM_PORTAL_CONFIGURATION_ID:
        process.env.STRIPE_ATRIAM_PORTAL_CONFIGURATION_ID,
      STRIPE_ATRIAM_PORTAL_SWITCH_CONFIGURATION_ID:
        process.env.STRIPE_ATRIAM_PORTAL_SWITCH_CONFIGURATION_ID,
      STRIPE_ATRIAM_PORTAL_LEGACY_CONFIGURATION_ID:
        process.env.STRIPE_ATRIAM_PORTAL_LEGACY_CONFIGURATION_ID,
      STRIPE_PLATFORM_WEBHOOK_SECRET:
        process.env.STRIPE_PLATFORM_WEBHOOK_SECRET,
      STRIPE_PLATFORM_PRICE_PRO_MONTHLY_ID:
        process.env.STRIPE_PLATFORM_PRICE_PRO_MONTHLY_ID,
      STRIPE_PLATFORM_PRICE_PRO_ANNUAL_ID:
        process.env.STRIPE_PLATFORM_PRICE_PRO_ANNUAL_ID,
      ATRIAM_AI_PLAN_LIMITS_ENABLED: process.env.ATRIAM_AI_PLAN_LIMITS_ENABLED,
      SIGNED_W9_ORPHAN_DELETE_ENABLED:
        process.env.SIGNED_W9_ORPHAN_DELETE_ENABLED,
      LOCAL_SEARCH_EXPORT_ENABLED: process.env.LOCAL_SEARCH_EXPORT_ENABLED,
      STRIPE_PLATFORM_PRICE_PRO_2026_MONTHLY_ID:
        process.env.STRIPE_PLATFORM_PRICE_PRO_2026_MONTHLY_ID,
      STRIPE_PLATFORM_PRICE_PRO_2026_ANNUAL_ID:
        process.env.STRIPE_PLATFORM_PRICE_PRO_2026_ANNUAL_ID,
      STRIPE_PLATFORM_PRICE_BUSINESS_MONTHLY_ID:
        process.env.STRIPE_PLATFORM_PRICE_BUSINESS_MONTHLY_ID,
      STRIPE_PLATFORM_PRICE_BUSINESS_ANNUAL_ID:
        process.env.STRIPE_PLATFORM_PRICE_BUSINESS_ANNUAL_ID,
      STRIPE_PLATFORM_PRICE_DONATION_ID:
        process.env.STRIPE_PLATFORM_PRICE_DONATION_ID,
      STRIPE_CONNECT_WEBHOOK_SECRET: process.env.STRIPE_CONNECT_WEBHOOK_SECRET,
      STRIPE_CONNECT_ACCOUNT_WEBHOOK_SECRET:
        process.env.STRIPE_CONNECT_ACCOUNT_WEBHOOK_SECRET,
      OPENAI_API_KEY: process.env.OPENAI_API_KEY,
      GEMINI_API_KEY: process.env.GEMINI_API_KEY,
      RAG_INDEX_INTERNAL_WEBHOOK_SECRET:
        process.env.RAG_INDEX_INTERNAL_WEBHOOK_SECRET,
      SPEND_CHECK_INTERNAL_WEBHOOK_SECRET:
        process.env.SPEND_CHECK_INTERNAL_WEBHOOK_SECRET,
      AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRET:
        process.env.AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRET,
      AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRETS:
        process.env.AGENT_CLI_USAGE_INTERNAL_WEBHOOK_SECRETS,
      OPENAI_AGENT_MODEL: process.env.OPENAI_AGENT_MODEL,
      AGENT_STEP_MODEL: process.env.AGENT_STEP_MODEL,
      AGENT_INTERNAL_WEBHOOK_SECRET: process.env.AGENT_INTERNAL_WEBHOOK_SECRET,
      AGENT_INTERNAL_WEBHOOK_SECRETS:
        process.env.AGENT_INTERNAL_WEBHOOK_SECRETS,
      JOB_PIPELINE_INTERNAL_WEBHOOK_SECRET:
        process.env.JOB_PIPELINE_INTERNAL_WEBHOOK_SECRET,
      JOB_PIPELINE_INTERNAL_WEBHOOK_SECRETS:
        process.env.JOB_PIPELINE_INTERNAL_WEBHOOK_SECRETS,
      LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRET:
        process.env.LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRET,
      LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRETS:
        process.env.LEAD_NOTIFY_INTERNAL_WEBHOOK_SECRETS,
      TELEGRAM_LEAD_BOT_TOKEN: process.env.TELEGRAM_LEAD_BOT_TOKEN,
      TELEGRAM_LEAD_BOT_USERNAME: process.env.TELEGRAM_LEAD_BOT_USERNAME,
      TELEGRAM_WEBHOOK_SECRET: process.env.TELEGRAM_WEBHOOK_SECRET,
      SEND_SMS_HOOK_SECRET: process.env.SEND_SMS_HOOK_SECRET,
      AUTH_OTP_GATEWAY_SECRET: process.env.AUTH_OTP_GATEWAY_SECRET,
      AUTH_OTP_TEST_NUMBERS: process.env.AUTH_OTP_TEST_NUMBERS,
      // Both were in the schema but missing from this pick since their
      // introduction — schema keys not named here are undefined at runtime
      // (guard: tests/unit/env/server-schema-pick-parity.test.ts).
      DEV_PREVIEW_EMAILS: process.env.DEV_PREVIEW_EMAILS,
      VERIFY_USER_EMAIL: process.env.VERIFY_USER_EMAIL,
      AUTOPILOT_DEFAULT_TIMEZONE: process.env.AUTOPILOT_DEFAULT_TIMEZONE,
      NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRET:
        process.env.NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRET,
      NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRETS:
        process.env.NOTIFY_EVENT_INTERNAL_WEBHOOK_SECRETS,
      VOICE_AGENT_APP_SECRET_ATRIAM: process.env.VOICE_AGENT_APP_SECRET_ATRIAM,
      VOICE_AGENT_APP_SECRETS_ATRIAM:
        process.env.VOICE_AGENT_APP_SECRETS_ATRIAM,
      REALTIME_BRIDGE_SECRET: process.env.REALTIME_BRIDGE_SECRET,
      REALTIME_BRIDGE_SECRETS: process.env.REALTIME_BRIDGE_SECRETS,
      TRANSCRIBE_INTERNAL_WEBHOOK_SECRET:
        process.env.TRANSCRIBE_INTERNAL_WEBHOOK_SECRET,
      TRANSCRIBE_INTERNAL_WEBHOOK_SECRETS:
        process.env.TRANSCRIBE_INTERNAL_WEBHOOK_SECRETS,
      ANALYZE_INTERNAL_WEBHOOK_SECRET:
        process.env.ANALYZE_INTERNAL_WEBHOOK_SECRET,
      ANALYZE_INTERNAL_WEBHOOK_SECRETS:
        process.env.ANALYZE_INTERNAL_WEBHOOK_SECRETS,
      PRICE_RANGE_INTERNAL_WEBHOOK_SECRET:
        process.env.PRICE_RANGE_INTERNAL_WEBHOOK_SECRET,
      PRICE_RANGE_INTERNAL_WEBHOOK_SECRETS:
        process.env.PRICE_RANGE_INTERNAL_WEBHOOK_SECRETS,
      ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRET:
        process.env.ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRET,
      ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRETS:
        process.env.ATTRIBUTE_CALL_INTERNAL_WEBHOOK_SECRETS,
      BANKING_INTERNAL_WEBHOOK_SECRET:
        process.env.BANKING_INTERNAL_WEBHOOK_SECRET,
      VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRET:
        process.env.VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRET,
      VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRETS:
        process.env.VERIFICATION_RECHECK_INTERNAL_WEBHOOK_SECRETS,
      STATEMENTS_INTERNAL_WEBHOOK_SECRET:
        process.env.STATEMENTS_INTERNAL_WEBHOOK_SECRET,
      STATEMENTS_INTERNAL_WEBHOOK_SECRETS:
        process.env.STATEMENTS_INTERNAL_WEBHOOK_SECRETS,
      // In the schema but missing from this pick since their introduction, so
      // they parsed as undefined no matter what Vercel env held.
      THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRET:
        process.env.THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRET,
      THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRETS:
        process.env.THREAD_SUGGEST_INTERNAL_WEBHOOK_SECRETS,
      PORTAL_SESSION_SECRET: process.env.PORTAL_SESSION_SECRET,
      PAYROLL_ENCRYPTION_KEY: process.env.PAYROLL_ENCRYPTION_KEY,
      SIGNING_SEAL_P12_BASE64: process.env.SIGNING_SEAL_P12_BASE64,
      SIGNING_SEAL_P12_PASSPHRASE: process.env.SIGNING_SEAL_P12_PASSPHRASE,
      APPLE_TEAM_ID: process.env.APPLE_TEAM_ID,
      ANDROID_SHA256_CERT_FINGERPRINT:
        process.env.ANDROID_SHA256_CERT_FINGERPRINT,
      PROREFERRAL_GATEWAY_URL: process.env.PROREFERRAL_GATEWAY_URL,
      GOOGLE_GEOCODING_API_KEY: process.env.GOOGLE_GEOCODING_API_KEY,
      GOOGLE_INDEXING_SA_JSON: process.env.GOOGLE_INDEXING_SA_JSON,
      GOOGLE_ADS_DEVELOPER_TOKEN: process.env.GOOGLE_ADS_DEVELOPER_TOKEN,
      GOOGLE_ADS_INTERNAL_WEBHOOK_SECRET:
        process.env.GOOGLE_ADS_INTERNAL_WEBHOOK_SECRET,
      GOOGLE_ADS_INTERNAL_WEBHOOK_SECRETS:
        process.env.GOOGLE_ADS_INTERNAL_WEBHOOK_SECRETS,
      GOOGLE_ADS_OAUTH_STATE_SECRET: process.env.GOOGLE_ADS_OAUTH_STATE_SECRET,
      GOOGLE_ADS_GATEWAY_URL: process.env.GOOGLE_ADS_GATEWAY_URL,
      GROWTH_ACQUISITION_CONNECTION: process.env.GROWTH_ACQUISITION_CONNECTION,
      GROWTH_REPORT_CONNECTIONS: process.env.GROWTH_REPORT_CONNECTIONS,
      FACEBOOK_ADS_APP_ID: process.env.FACEBOOK_ADS_APP_ID,
      FACEBOOK_ADS_APP_SECRET: process.env.FACEBOOK_ADS_APP_SECRET,
      FACEBOOK_ADS_OAUTH_STATE_SECRET:
        process.env.FACEBOOK_ADS_OAUTH_STATE_SECRET,
      FB_LEADGEN_VERIFY_TOKEN: process.env.FB_LEADGEN_VERIFY_TOKEN,
      GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY,
      FACEBOOK_GATEWAY_URL: process.env.FACEBOOK_GATEWAY_URL,
      GOOGLE_OAUTH_CLIENT_ID: process.env.GOOGLE_OAUTH_CLIENT_ID,
      GOOGLE_OAUTH_CLIENT_SECRET: process.env.GOOGLE_OAUTH_CLIENT_SECRET,
      GOOGLE_PUBSUB_TOPIC: process.env.GOOGLE_PUBSUB_TOPIC,
      GOOGLE_PUBSUB_VERIFICATION_TOKEN:
        process.env.GOOGLE_PUBSUB_VERIFICATION_TOKEN,
      ELEVENLABS_API_KEY: process.env.ELEVENLABS_API_KEY,
      OPENAI_BASE_URL: process.env.OPENAI_BASE_URL,
      CRON_SECRET: process.env.CRON_SECRET,
      INTERNAL_SERVICE_SECRET: process.env.INTERNAL_SERVICE_SECRET,
      THUMBTACK_GATEWAY_URL: process.env.THUMBTACK_GATEWAY_URL,
      HD_CLIENT_ID: process.env.HD_CLIENT_ID,
      HD_CLIENT_SECRET: process.env.HD_CLIENT_SECRET,
      PLAID_CLIENT_ID: process.env.PLAID_CLIENT_ID,
      PLAID_SECRET: process.env.PLAID_SECRET,
      PLAID_ENV: process.env.PLAID_ENV,
      PLAID_WEBHOOK_URL: process.env.PLAID_WEBHOOK_URL,
      BANKING_TOKEN_ENCRYPTION_KEY: process.env.BANKING_TOKEN_ENCRYPTION_KEY,
      BANKING_TOKEN_ENCRYPTION_KEY_ID:
        process.env.BANKING_TOKEN_ENCRYPTION_KEY_ID,
      BANKING_TOKEN_ENCRYPTION_KEYS: process.env.BANKING_TOKEN_ENCRYPTION_KEYS,
      QUICKBOOKS_CLIENT_ID: process.env.QUICKBOOKS_CLIENT_ID,
      QUICKBOOKS_CLIENT_SECRET: process.env.QUICKBOOKS_CLIENT_SECRET,
      QUICKBOOKS_ENV: process.env.QUICKBOOKS_ENV,
      WORK_DOCS_TOKEN_SECRET: process.env.WORK_DOCS_TOKEN_SECRET,
      CLAIM_COOKIE_SECRET: process.env.CLAIM_COOKIE_SECRET,
      COMMUNITY_ADMIN_EMAILS: process.env.COMMUNITY_ADMIN_EMAILS,
      OTEL_EXPORTER_OTLP_ENDPOINT: process.env.OTEL_EXPORTER_OTLP_ENDPOINT,
      SIGNOZ_INGEST_TOKEN: process.env.SIGNOZ_INGEST_TOKEN,
      SIGNOZ_TELEGRAM_BOT_TOKEN: process.env.SIGNOZ_TELEGRAM_BOT_TOKEN,
      SIGNOZ_TELEGRAM_CHAT_ID: process.env.SIGNOZ_TELEGRAM_CHAT_ID,
      BILLING_INVITE_TELEGRAM_USER_IDS:
        process.env.BILLING_INVITE_TELEGRAM_USER_IDS,
      SIGNOZ_TELEGRAM_CRITICAL_CHAT_ID:
        process.env.SIGNOZ_TELEGRAM_CRITICAL_CHAT_ID,
      FEEDBACK_TELEGRAM_CHAT_ID: process.env.FEEDBACK_TELEGRAM_CHAT_ID,
      SIGNOZ_ALERT_WEBHOOK_TOKEN: process.env.SIGNOZ_ALERT_WEBHOOK_TOKEN,
      POSTHOG_ALERT_WEBHOOK_SECRET: process.env.POSTHOG_ALERT_WEBHOOK_SECRET,
      POSTHOG_CLI_API_KEY: process.env.POSTHOG_CLI_API_KEY,
      POSTHOG_CLI_PROJECT_ID: process.env.POSTHOG_CLI_PROJECT_ID,
      SITECALL_POSTHOG_CLI_API_KEY: process.env.SITECALL_POSTHOG_CLI_API_KEY,
      SITECALL_POSTHOG_CLI_PROJECT_ID:
        process.env.SITECALL_POSTHOG_CLI_PROJECT_ID,
      VERCEL_WEBHOOK_SECRET: process.env.VERCEL_WEBHOOK_SECRET,
      ATRIAM_SERVICE_CLAIM_SECRET: process.env.ATRIAM_SERVICE_CLAIM_SECRET,
      EXEMPT_WORKSPACE_IDS: process.env.EXEMPT_WORKSPACE_IDS,
      DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRET:
        process.env.DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRET,
      DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRETS:
        process.env.DOCUMENTS_EXTRACT_INTERNAL_WEBHOOK_SECRETS,
      AGENT_STEP_INTERNAL_WEBHOOK_SECRET:
        process.env.AGENT_STEP_INTERNAL_WEBHOOK_SECRET,
      AGENT_STEP_INTERNAL_WEBHOOK_SECRETS:
        process.env.AGENT_STEP_INTERNAL_WEBHOOK_SECRETS,
      AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRET:
        process.env.AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRET,
      AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRETS:
        process.env.AGENT_SIGNAL_INTERNAL_WEBHOOK_SECRETS,
      DISPATCH_INTERNAL_WEBHOOK_SECRET:
        process.env.DISPATCH_INTERNAL_WEBHOOK_SECRET,
      DISPATCH_INTERNAL_WEBHOOK_SECRETS:
        process.env.DISPATCH_INTERNAL_WEBHOOK_SECRETS,
      CRON_REPORT_WEBHOOK_SECRET: process.env.CRON_REPORT_WEBHOOK_SECRET,
      DATA_STASH_INTERNAL_WEBHOOK_SECRET:
        process.env.DATA_STASH_INTERNAL_WEBHOOK_SECRET,
      DATA_STASH_INTERNAL_WEBHOOK_SECRETS:
        process.env.DATA_STASH_INTERNAL_WEBHOOK_SECRETS,
      PRIVACY_OPERATOR_USER_IDS: process.env.PRIVACY_OPERATOR_USER_IDS,
      OPERATOR_USER_IDS: process.env.OPERATOR_USER_IDS,
      OPERATOR_API_TOKEN: process.env.OPERATOR_API_TOKEN,
      GARAGE_S3_ENDPOINT: process.env.GARAGE_S3_ENDPOINT,
      GARAGE_BUCKET: process.env.GARAGE_BUCKET,
      GARAGE_REGION: process.env.GARAGE_REGION,
      GARAGE_ACCESS_KEY_ID: process.env.GARAGE_ACCESS_KEY_ID,
      GARAGE_SECRET_ACCESS_KEY: process.env.GARAGE_SECRET_ACCESS_KEY,
    })
  : ({} as z.infer<typeof serverSchema>);

export type ServerEnv = typeof serverEnv;
